import React, { useRef, useState } from 'react';
import emailjs from 'emailjs-com';
import Button from '../shared/components/Button';
import { Estimate, inquiryBreakdown, startingPrice } from './pricing';
import { Message, Panel } from './styles';

export default function InquiryForm({ estimate }: { estimate: Estimate }) {
	const [name, setName] = useState('');
	const [date, setDate] = useState('');
	const [phone, setPhone] = useState('');
	const [sending, setSending] = useState(false);
	const lock = useRef(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);

	const submit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (lock.current) return;

		setError('');
		setSuccess(false);

		if (
			name.trim().length < 3 ||
			!/^0(5[0-689]|[2-4]|[8-9]|7[0-9])[0-9]{7}$/.test(
				phone.replace(/[\s-]/g, '')
			) ||
			!date ||
			!estimate.rows.length
		) {
			setError(
				'יש להזין שם מלא (לפחות 3 תווים), תאריך חתונה, מספר טלפון ישראלי תקין ולבחור שירות אחד לפחות.'
			);

			return;
		}

		const form = e.currentTarget;
		lock.current = true;
		setSending(true);

		try {
			if (
				!process.env.REACT_APP_EMAILJS_SERVICE ||
				!process.env.REACT_APP_EMAILJS_TEMPLATE ||
				!process.env.REACT_APP_EMAILJS_USER
			)
				throw new Error('Missing configuration');

			await emailjs.sendForm(
				process.env.REACT_APP_EMAILJS_SERVICE,
				process.env.REACT_APP_EMAILJS_TEMPLATE,
				form,
				process.env.REACT_APP_EMAILJS_USER
			);
			setSuccess(true);
		} catch {
			setError(
				'שליחת הבקשה נכשלה. הפרטים והבחירה נשמרו כאן - נסו שוב, או פנו אל focusevent25@gmail.com.'
			);
		} finally {
			lock.current = false;
			setSending(false);
		}
	};

	return (
		<Panel id="inquiry">
			<h2>בחרתם? בואו נדייק את הפרטים יחד</h2>
			<p>
				השאירו פרטים ונחזור אליכם לשיחה על השירותים שבחרתם ועל ההתאמה שלהם
				לחתונה שלכם. שליחת הבקשה אינה מחייבת הזמנה.
			</p>
			<p>
				{estimate.rows
					.map(
						(r) =>
							`${r.service.name}${r.includers.length ? ' (כלול במחיר)' : ''}`
					)
					.join(' · ') || 'בחרו שירותים כדי לשלוח בקשה.'}
			</p>
			<p>
				<strong>הערכת המחיר: {startingPrice(estimate.totalCents)}</strong>
			</p>
			<form onSubmit={submit} aria-label="בקשת הצעה מותאמת">
				<fieldset disabled={sending || success}>
					<label htmlFor="inquiry-name">
						שם מלא
						<input
							id="inquiry-name"
							name="coupleName"
							required
							minLength={3}
							maxLength={160}
							autoComplete="name"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</label>
					<label htmlFor="inquiry-date">
						תאריך החתונה
						<input
							id="inquiry-date"
							name="weddingDate"
							type="date"
							required
							value={date}
							onClick={(e) => {
								const input = e.currentTarget as HTMLInputElement & {
									showPicker?: () => void;
								};

								input.showPicker?.();
							}}
							onChange={(e) => setDate(e.target.value)}
						/>
					</label>
					<label htmlFor="inquiry-phone">
						מספר טלפון
						<input
							id="inquiry-phone"
							name="phoneNumber"
							type="tel"
							required
							autoComplete="tel"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
						/>
					</label>
					<input
						type="hidden"
						name="comments"
						value={inquiryBreakdown(estimate)}
					/>
					<Button
						text={sending ? 'שולחים את הבקשה…' : 'שלחו לי הצעה מותאמת'}
						type="submit"
						color="mainDark"
						bgColor="secDark"
						disabled={sending || success || !estimate.rows.length}
					/>
				</fieldset>
			</form>
			{error && <Message role="alert">{error}</Message>}
			{success && (
				<p role="status">
					הבקשה נשלחה בהצלחה. נחזור אליכם להמשך התאמה אישית. זו פנייה לקבלת הצעה
					ואינה הזמנה מאושרת.
				</p>
			)}
		</Panel>
	);
}
